import { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Typography,
} from '@mui/material';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import PendingIcon from '@mui/icons-material/Pending';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { SeoKeywordOpportunity, OpportunityStatus, FixGuidance, AiSuggestion, SeoTask } from '@/types/api/seo';
import { PlaybookStepData } from './playbookConfig';
import OpportunityCard from './OpportunityCard';

type Props = {
  step: PlaybookStepData;
  phaseColor: string;
  isLast: boolean;
  defaultExpanded?: boolean;
  onStatusChange: (id: string, status: OpportunityStatus) => void;
  onGetFixGuidance: (opportunity: SeoKeywordOpportunity) => Promise<FixGuidance | null>;
  onGetSuggestions: (opportunity: SeoKeywordOpportunity) => Promise<AiSuggestion[] | null>;
  onApplyToWordPress: (opportunityId: string, suggestion: AiSuggestion, pageUrl: string | null) => Promise<{ success: boolean; error?: string }>;
  wpConnected: boolean;
  rankingPageUrl: string | null;
  currentMetrics: { position: number | null; ctr: number | null; clicks: number | null; impressions: number | null };
  onGenerateStepSuggestions: (stepId: string, stepLabel: string, stepDescription: string) => Promise<void>;
  onAddTask: (suggestion: AiSuggestion, opportunityId: string) => Promise<boolean>;
  existingTasks: SeoTask[];
};

const statusIcon = (status: string, color: string) => {
  if (status === 'completed') return <CheckCircleIcon sx={{ fontSize: 20, color: '#0D9D58' }} />;
  if (status === 'in_progress') return <PendingIcon sx={{ fontSize: 20, color: '#F4B400' }} />;
  if (status === 'needs_review') return <HelpOutlineIcon sx={{ fontSize: 20, color: '#F4B400' }} />;
  return <RadioButtonUncheckedIcon sx={{ fontSize: 20, color: '#D0D0D0' }} />;
};

const PlaybookStep = function ({
  step,
  phaseColor,
  isLast,
  defaultExpanded = false,
  onStatusChange,
  onGetFixGuidance,
  onGetSuggestions,
  onApplyToWordPress,
  wpConnected,
  rankingPageUrl,
  currentMetrics,
  onGenerateStepSuggestions,
  onAddTask,
  existingTasks,
}: Props): JSX.Element {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [generating, setGenerating] = useState(false);
  const hasFindings = step.opportunities.length > 0;
  const isNeedsReview = step.status === 'needs_review';

  return (
    <Box sx={{ display: 'flex', gap: 1.5 }}>
      {/* Timeline connector */}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 20, flexShrink: 0 }}>
        {statusIcon(step.status, phaseColor)}
        {!isLast && (
          <Box sx={{ width: 2, flex: 1, backgroundColor: step.status === 'completed' ? '#0D9D58' : '#E0E0E0', mt: 0.5 }} />
        )}
      </Box>

      {/* Step content */}
      <Box sx={{ flex: 1, mb: isLast ? 0 : 1.5 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            cursor: (hasFindings || isNeedsReview) ? 'pointer' : 'default',
            py: 0.5,
            '&:hover': (hasFindings || isNeedsReview) ? { opacity: 0.8 } : {},
          }}
          onClick={() => (hasFindings || isNeedsReview) && setExpanded(!expanded)}
        >
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" component="div" fontWeight={600} sx={{ fontSize: 13, color: step.status === 'completed' ? '#0D9D58' : 'text.primary' }}>
              {step.label}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
              {step.description}
            </Typography>
          </Box>

          {hasFindings && step.status !== 'completed' && (
            <Chip
              label={`${step.opportunities.filter((o) => o.status === 'open' || o.status === 'in_progress').length} ${step.opportunities.length === 1 ? 'finding' : 'findings'}`}
              size="small"
              sx={{ fontSize: 10, height: 20, backgroundColor: `${phaseColor}15`, color: phaseColor }}
            />
          )}

          {step.status === 'completed' && (
            <Chip label="Done" size="small" color="success" sx={{ fontSize: 10, height: 20 }} />
          )}

          {step.status === 'needs_review' && (
            <Chip label="Needs improvement" size="small" color="warning" sx={{ fontSize: 10, height: 20 }} />
          )}

          {!hasFindings && step.status === 'not_started' && (
            <Chip label="Not analyzed" size="small" sx={{ fontSize: 10, height: 20 }} />
          )}

          {(hasFindings || isNeedsReview) && (
            <IconButton size="small" sx={{ p: 0.25 }}>
              {expanded ? <ExpandLessIcon sx={{ fontSize: 18 }} /> : <ExpandMoreIcon sx={{ fontSize: 18 }} />}
            </IconButton>
          )}
        </Box>

        <Collapse in={expanded}>
          <Box sx={{ mt: 1, pl: 0.5 }}>
            {/* Needs review — no issues found but not in top 3 */}
            {isNeedsReview && !hasFindings && (
              <Box sx={{
                p: 2,
                mb: 1,
                borderRadius: 2,
                backgroundColor: '#FFF8E1',
                border: '1px solid #F4B40020',
              }}>
                <Typography variant="body2" component="div" sx={{ fontSize: 12, mb: 1 }}>
                  No specific issues detected, but your keyword isn't in the top 3 yet. This area may still need improvement to outrank competitors.
                </Typography>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={generating ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <LightbulbIcon sx={{ fontSize: 16 }} />}
                  onClick={async (e) => {
                    e.stopPropagation();
                    setGenerating(true);
                    await onGenerateStepSuggestions(step.id, step.label, step.description);
                    setGenerating(false);
                  }}
                  disabled={generating}
                  sx={{
                    backgroundColor: '#F4B400',
                    '&:hover': { backgroundColor: '#E0A600' },
                    textTransform: 'none',
                    fontSize: 12,
                  }}
                >
                  {generating ? 'Generating...' : 'Generate Improvements'}
                </Button>
              </Box>
            )}

            {step.opportunities.map((opp) => (
              <OpportunityCard
                key={opp.id}
                opportunity={opp}
                onStatusChange={onStatusChange}
                onGetFixGuidance={onGetFixGuidance}
                onGetSuggestions={onGetSuggestions}
                onApplyToWordPress={onApplyToWordPress}
                onAddTask={onAddTask}
                existingTasks={existingTasks}
                wpConnected={wpConnected}
                rankingPageUrl={rankingPageUrl}
                currentMetrics={currentMetrics}
              />
            ))}
          </Box>
        </Collapse>
      </Box>
    </Box>
  );
};

export default PlaybookStep;
